/**
 * useGetDeductions
 * React Query hook for fetching all deductions for a pot period (admin view)
 */

import { useQuery } from '@tanstack/react-query';
import type { AdminDeductionRow } from '@billda/api';
import { getDeductions } from '../api/deductionsAdminApi';
import { useAuthStore } from '../../auth/stores/authStore';

export const deductionsKeys = {
  all: ['deductions'] as const,
  list: (potPeriodId: string) =>
    [...deductionsKeys.all, 'list', { potPeriodId }] as const,
};

export interface UseGetDeductionsReturn {
  deductions: AdminDeductionRow[];
  totalCount: number;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch all deductions for a period (admin view)
 *
 * @param potPeriodId - The pot period ID to fetch deductions for
 */
export function useGetDeductions(
  potPeriodId: string | null
): UseGetDeductionsReturn {
  const isAuthenticated = useAuthStore(state => state.isAuthenticated);

  const { data, isLoading, error } = useQuery({
    queryKey: deductionsKeys.list(potPeriodId ?? ''),
    queryFn: async ({ signal }) => {
      if (!potPeriodId) {
        throw new Error('No pot period selected');
      }
      return getDeductions(potPeriodId, signal);
    },
    enabled: isAuthenticated && !!potPeriodId,
    staleTime: 30000, // 30 seconds
  });

  return {
    deductions: data?.deductions ?? [],
    totalCount: data?.totalCount ?? 0,
    isLoading,
    error: error as Error | null,
  };
}
